import { useActivityStore, type Activity, type ActivitySource } from './activityStore';

/**
 * Wrap a client-driven async/SSE producer ONCE so its lifecycle mirrors into the
 * Activity Center: upsert on start, appendLog / appendStream while it runs, and
 * complete/fail at the end. Every UI-kicked background task should run through
 * this, so the user always sees "what's running right now" in one place.
 *
 * The wrapped `fn` receives a small reporter whose methods all funnel into
 * `useActivityStore`, plus a bare `onProgress(message)` for producers (like the
 * existing SSE consumers) that only emit progress strings. `runWithProgressToast`
 * can become a thin shim over this (same outer signature) so no callsite changes.
 */

export interface TrackActivityMeta {
  /** Stable id; auto-generated when omitted. */
  id?: string;
  source: ActivitySource;
  /** Reuses SyncRun.kind vocabulary so eta.ts KIND_ICON / kindGroup apply. */
  kind: string;
  title: string;
  message?: string;
  progress?: { phase?: string; done: number; total: number } | null;
  stoppable?: boolean;
  /** Set/learn the linked /api/syncs row id (also de-dups the count). */
  serverRunId?: string;
  href?: { label: string; to: string };
}

/** Reporter handed to the wrapped producer — all writes hit the activity store. */
export interface ActivityReporter {
  /** This activity's id. */
  id: string;
  /** Append an info log line. */
  log: (text: string, type?: 'info' | 'success' | 'error') => void;
  /** Append AI streaming output. */
  stream: (chunk: string) => void;
  /** Reset the streaming buffer (e.g. between items in a batch). */
  clearStream: () => void;
  /** Patch the activity (progress, message, serverRunId, stoppable, …). */
  update: (patch: Partial<Activity>) => void;
  /** Set determinate progress in one call. */
  progress: (done: number, total: number, phase?: string) => void;
  /** Bare progress-string sink — mirrors `runWithProgressToast`'s onProgress. */
  onProgress: (message: string) => void;
}

/**
 * Run `fn` as a tracked Activity. Resolves to whatever `fn` returns; on throw the
 * activity is marked failed (sticky) and the error re-thrown so callers keep
 * their existing try/catch + terminal toast behaviour.
 */
export async function trackActivity<T>(
  meta: TrackActivityMeta,
  fn: (reporter: ActivityReporter) => Promise<T>,
): Promise<T> {
  const store = useActivityStore.getState();
  const id = meta.id ?? (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `act-${Date.now()}-${Math.random().toString(36).slice(2)}`);

  store.upsert({
    id,
    source: meta.source,
    kind: meta.kind,
    title: meta.title,
    status: 'running',
    message: meta.message,
    progress: meta.progress ?? null,
    stoppable: meta.stoppable,
    serverRunId: meta.serverRunId,
    href: meta.href,
    logs: [],
    streamingContent: '',
    startedAt: new Date().toISOString(),
  });

  const reporter: ActivityReporter = {
    id,
    log: (text, type = 'info') => useActivityStore.getState().appendLog(id, { type, text }),
    stream: (chunk) => useActivityStore.getState().appendStream(id, chunk),
    clearStream: () => useActivityStore.getState().clearStream(id),
    update: (patch) => useActivityStore.getState().update(id, patch),
    progress: (done, total, phase) => useActivityStore.getState().update(id, { progress: { done, total, phase } }),
    onProgress: (message) => {
      const s = useActivityStore.getState();
      if (message) s.appendLog(id, { type: 'info', text: message });
      s.update(id, { message: message || meta.message });
    },
  };

  try {
    const result = await fn(reporter);
    useActivityStore.getState().complete(id, { message: 'Done' });
    return result;
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    useActivityStore.getState().fail(id, msg);
    throw e;
  }
}
